<template>
  <v-footer>
    <div class="footer_logo_section">
      <img :src="getResourceUrl(`/cdn/${sportTitle}/site/logo-combi.svg`)" :alt="`${sportTitle}Levels`" />
    </div>
    
    <div class="footer_sitemap_section">
      <div class="footer_sitemap_div footer_sitemap_socials">
        <v-card color="secondary" title="Social" density="compact" variant="text"> </v-card>
        <div class="footer_sitemap_socialIcons">
          <v-btn :href="`https://twitter.com/${normalizedSportTitle(sportTitle)}Levels`" icon target="_blank" variant="text" :ripple="false">
            <img src="@/assets/icons/icon-x-social-media-white.svg" alt="Twitter" style="height: 20px; width: 22px" />
          </v-btn>
          <v-btn :href="`https://www.facebook.com/${normalizedSportTitle(sportTitle)}Levels`" icon target="_blank" variant="text">
            <v-icon size="24px">mdi-facebook</v-icon>
          </v-btn>
          <v-btn :href="`https://www.instagram.com/${normalizedSportTitle(sportTitle)}Levels`" icon target="_blank" variant="text">
            <v-icon size="24px">mdi-instagram</v-icon>
          </v-btn>
        </div>
      </div>
      
      <!-- Skeleton Loader for "SquashLevels" Section -->
      <div class="footer_sitemap_div" v-if="loading">
        <v-card color="secondary" title="SquashLevels" density="compact" variant="text"> </v-card>
        <v-skeleton-loader class="footer_skeleton_loader" width="250px" max-width="350" color="transparent" type="list-item" v-for="n in 5" :key="n"> </v-skeleton-loader>
      </div>
      
      <!-- Actual "SquashLevels" List Items -->
      <div class="footer_sitemap_div" v-else>
        <v-card color="secondary" title="Members Area" density="compact" variant="text"> </v-card>
        <v-list lines="one" v-if="props.memberData && props.memberData.href">
          <v-list-item
          v-for="(item, key) in props.memberData.href"
          :key="key"
          v-bind="item.href?.startsWith('http') ? { href: item.href, target: '_blank' } : { to: '/' + item.href }"
          :title="key"
          color="#fff"
          variant="plain"
          :ripple="false"
          >
          <!-- Additional content can go here -->
        </v-list-item>
      </v-list>
    </div>
    
    <!-- Skeleton Loader for "My Rankings" Section -->
    <div class="footer_sitemap_div" v-if="loading">
      <v-card color="secondary" title="My Rankings" density="compact" variant="text"> </v-card>
      <v-skeleton-loader class="footer_skeleton_loader px-0" width="255px" max-width="350" color="transparent" type="list-item" v-for="n in 5" :key="n"> </v-skeleton-loader>
    </div>
    
    <!-- Actual "My Rankings" List Items -->
    <div class="footer_sitemap_div" v-else>
      <v-card color="secondary" title="My Rankings" density="compact" variant="text"> </v-card>
      <v-list lines="one" v-if="props.myRankingsData && props.myRankingsData.href">
        <v-list-item
        v-for="(item, key) in props.myRankingsData.href"
        :key="key"
        v-bind="item.href?.startsWith('http') ? { href: item.href, target: '_blank' } : { to: '/' + item.href }"
        :title="key"
        color="#fff"
        variant="plain"
        :ripple="false"
        >
        <!-- Additional content can go here -->
      </v-list-item>
    </v-list>
  </div>
  
  <!-- Skeleton Loader for "More" Section -->
  <div class="footer_sitemap_div" v-if="loading">
    <v-card color="secondary" title="More" density="compact" variant="text" />
    <v-skeleton-loader
    class="footer_skeleton_loader px-0"
    width="255px"
    max-width="350"
    color="transparent"
    type="list-item"
    v-for="n in 5"
    :key="n"
    />
  </div>
  
  <!-- Actual "More" List Items -->
  <div class="footer_sitemap_div" v-else>
    <v-card color="secondary" title="More" density="compact" variant="text" />
    <v-list lines="one" v-if="props.moreData && props.moreData.href">
      <v-list-item
      v-for="(item, key) in props.moreData.href"
      :key="key"
      v-bind="item.href?.startsWith('http') ? { href: item.href, target: '_blank' } : { to: '/' + item.href }"
      :title="key"
      color="#fff"
      variant="plain"
      :ripple="false"
      />
    </v-list>
  </div>
  
</div>

<div class="footer_copyright_section">
  <p>© 2006-{{ currYear }}, {{ sportTitle }}Levels Ltd. Page time: {{ loadTime }}s</p>
</div>
</v-footer>
</template>

<script lang="ts" setup>
import { ref, watchEffect } from 'vue';
import { getResourceUrl, sportTitle } from '@/services/admin_actions/apiUtils';
import { getTotalTime, logTimers } from '@/services/admin_actions/pageTimerService';

// Define the props that this component expects
const props = defineProps({
  memberData: Object,
  myRankingsData: Object,
  moreData: Object, 
});

// Initialize the current year
const currYear = new Date().getFullYear();

// Add loading state to show the skeleton loader
const loading = ref(true); // Set loading to true initially

// Function to normalize sport titles for social media links & check for "squash57"
const normalizedSportTitle = (title: string) => {
  const result = title.toLowerCase() === "squash57" ? "squash" : title
  return result
}

// Watch memberData and myRankingsData and set loading to false when data is loaded
const loadTime = ref(0);
document.addEventListener('body_content_loaded', () => {
  setTimeout(() => {
    loadTime.value = parseFloat((getTotalTime() / 1000).toFixed(2));
    logTimers();
  }, 100);
});

watchEffect(() => {
  if (props.memberData && props.myRankingsData) {
    loading.value = false;
  }
});
</script>

<style lang="scss">
// import the Sass Variables File

body .v-footer {
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  padding: 0px;
  position: relative;
  transition: 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  transition-property: height, width, transform, max-width, left, right, top, bottom;
  border-color: rgba(var(--v-border-color), var(--v-border-opacity));
  border-style: solid;
  border-width: 0;
  box-shadow:
  0px 0px 0px 0px var(--v-shadow-key-umbra-opacity, rgba(0, 0, 0, 0.2)),
  0px 0px 0px 0px var(--v-shadow-key-penumbra-opacity, rgba(0, 0, 0, 0.14)),
  0px 0px 0px 0px var(--v-shadow-key-penumbra-opacity, rgba(0, 0, 0, 0.12));
  border-radius: 0;
  background: rgb(var(--v-theme-surface));
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  @include display_flex_center;
  flex-direction: column;
  // z-index: 100000;
}

// Footer Logo Section Styling

.footer_logo_section {
  padding: 20px 0px;
  margin: 0px;
  width: 100%;
  height: 100px;
  background-color: rgb(var(--v-theme-primary));
  @include display_flex_center;
  flex-direction: column;
}

.footer_logo_section img {
  height: 50px;
  width: 260px;
}

// Footer Sitemap Section Styling
.footer_sitemap_section {
  margin: 0px;
  padding: 0px 80px;
  width: 100%;
  min-height: 186px;
  background-color: rgb(var(--v-theme-primary-600));
  @include display_flex_center;
  flex-direction: row;
  align-items: stretch;
  gap: 0px;
  flex-wrap: wrap;
  margin: auto;
}

.footer_sitemap_div {
  border-left: solid 2px rgb(var(--v-theme-primary-800));
  display: flex;
  flex-direction: column;
  padding: 30px 20px;
  color: white;
  flex: 1 1 250px;
}

@media (min-width: 1214px) {
  .footer_sitemap_div:first-child {
    border-left: 0px;
  }
}

.footer_sitemap_section {
  .v-card-title {
    @include h1-global;
    font-size: 12px;
    text-align: left;
    padding: 10px 0px 10px 0px;
    margin: 0px;
  }
  .v-card-item {
    padding: 0px;
  }
}

.footer_sitemap_socials {
  display: flex;
  align-content: flex-start;
  justify-content: flex-start;
  align-items: flex-start;
  flex-direction: column;
}

.footer_sitemap_socialIcons {
  @include display_flex_center;
  margin-left: -10px;
  gap: 0px;
}

.footer_sitemap_socialIcons i {
  color: $white;
  font-size: 24px;
}

.footer_sitemap_div .v-list {
  padding: 0px;
  background: transparent;
}

.footer_sitemap_div .v-list-item {
  padding: 3px 0px !important;
  min-height: 0px !important;
}

.footer_sitemap_div .v-list-item-title {
  font-size: 11px;
  color: $white;
  letter-spacing: 3px;
  font-family: $brandon-grotesque-font;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  letter-spacing: 3px;
  text-transform: uppercase;
  align-items: center;
  text-wrap: balance;
  text-align: start;
}

.footer_sitemap_div .v-list-item-title:hover {
  color: (var(--v-theme-secondary));
  font-size: 11px;
  letter-spacing: 3px;
  font-family: $brandon-grotesque-font;
  font-weight: bold;
  font-style: normal;
  font-stretch: normal;
  letter-spacing: 3px;
  text-transform: uppercase;
  cursor: pointer;
}

// Footer Skeleton Loader Styling

.footer_skeleton_loader {
  .v-skeleton-loader__list-item {
    margin: 10px 0px;
  }
  
  .v-skeleton-loader__bone::after {
    @include global_skeleton_loader;
    // animation: loading 2.5s infinite !important;
    // background: linear-gradient(90deg, rgba(var(--v-theme-surface), 0), rgba(var(--v-theme-surface), 0.03), rgba(var(--v-theme-surface), 0)) !important;
  }
}

// Footer Copyright Section Styling

.footer_copyright_section {
  width: 100%;
  height: 50px;
  margin: 0px;
  padding: 18px 0px;
  border-top: solid 2px rgb(var(--v-theme-primary-800));
  background-color: rgb(var(--v-theme-primary-600));
  @include display_flex_center;
  flex-direction: column;
  color: $muted-purple;
  font-family: $english-grotesque-font;
  font-weight: 300;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.35;
  letter-spacing: normal;
  text-transform: capitalize;
  font-size: 14px;
}

// Hide Site Footer on Mobile & Tablet
@media (max-width: $mobile-screen) {
  .footer_logo_section,
  .footer_sitemap_section,
  .footer_copyright_section {
    display: none;
  }
}
</style>
